import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, RefreshCw, MessageSquare, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const statusColors = { reserved: "secondary", confirmed: "default", payment_pending: "outline", cancelled: "destructive", no_show: "destructive" };
const paymentColors = { pending: "outline", paid: "default", failed: "destructive", refunded: "secondary" };

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [filterEvent, setFilterEvent] = useState("");

  const fetchBookings = useCallback(() => {
    const url = filterEvent ? `${API}/admin/bookings?event_id=${filterEvent}` : `${API}/admin/bookings`;
    axios.get(url, authHeaders()).then(r => setBookings(r.data)).catch(() => {});
  }, [filterEvent]);

  useEffect(() => {
    axios.get(`${API}/admin/events`, authHeaders()).then(r => setEvents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/bookings/${id}/status`, { status }, authHeaders());
    toast.success(`Status: ${status}`); fetchBookings();
  };

  const updatePayment = async (id, status) => {
    await axios.put(`${API}/admin/bookings/${id}/payment-status`, { status }, authHeaders());
    toast.success(`Payment: ${status}`); fetchBookings();
  };

  const resendWhatsApp = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/bookings/${id}/resend-whatsapp`, {}, authHeaders());
      r.data.success ? toast.success("WhatsApp sent") : toast.error(r.data.error || "Failed");
    } catch { toast.error("Failed"); }
  };
  
  const sendEmail = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/bookings/${id}/send-email`, {}, authHeaders());
      r.data.success ? toast.success("Email sent") : toast.error(r.data.error || "Failed");
    } catch { toast.error("Failed"); }
  };

  const exportCSV = () => {
    const url = filterEvent ? `${API}/admin/bookings/export?event_id=${filterEvent}` : `${API}/admin/bookings/export`;
    window.open(`${url}`, "_blank");
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl tracking-tight font-medium">Bookings ({bookings.length})</h1>
        <div className="flex items-center gap-2">
          <select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} className="h-8 rounded-sm border border-input bg-transparent px-2 text-xs" data-testid="admin-bookings-filter">
            <option value="">All events</option>
            {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
          </select>
          <Button variant="outline" size="sm" className="gap-1 rounded-sm text-xs h-8" onClick={exportCSV} data-testid="admin-export-bookings"><Download className="w-3 h-3" /> CSV</Button>
        </div>
      </div>

      <div className="space-y-2">
        {bookings.map((b) => (
          <div key={b.id} className="bg-card border border-border/40 rounded-sm p-4" data-testid={`admin-booking-${b.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-sm">{b.fullName}</span>
                  <Badge variant={statusColors[b.bookingStatus] || "secondary"} className="text-[10px] rounded-full">{b.bookingStatus}</Badge>
                  <Badge variant={paymentColors[b.paymentStatus] || "outline"} className="text-[10px] rounded-full">Pay: {b.paymentStatus}</Badge>
                  {b.whatsappOptIn && <Badge variant="secondary" className="text-[10px] rounded-full">WA</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{b.email} / {b.phoneNumber} / {b.eventName} / {b.venueArea} / {b.eventDate}</p>
                {b.utrNumber && <p className="text-xs mt-1 text-foreground font-medium">UTR: {b.utrNumber}</p>}
                {(b.realtimePhotoUrl || b.uploadedPhotoUrl) && (
                  <div className="flex gap-3 mt-2">
                    {b.realtimePhotoUrl && (
                      <a href={b.realtimePhotoUrl.startsWith('http') ? b.realtimePhotoUrl : `${process.env.REACT_APP_BACKEND_URL}${b.realtimePhotoUrl}`} target="_blank" rel="noreferrer" title="Live Photo">
                        <img src={b.realtimePhotoUrl.startsWith('http') ? b.realtimePhotoUrl : `${process.env.REACT_APP_BACKEND_URL}${b.realtimePhotoUrl}`} alt="Live Photo" className="w-12 h-12 object-cover rounded-md border hover:opacity-80 transition-opacity" />
                      </a>
                    )}
                    {b.uploadedPhotoUrl && (
                      <a href={b.uploadedPhotoUrl.startsWith('http') ? b.uploadedPhotoUrl : `${process.env.REACT_APP_BACKEND_URL}${b.uploadedPhotoUrl}`} target="_blank" rel="noreferrer" title="Uploaded ID/Photo">
                        <img src={b.uploadedPhotoUrl.startsWith('http') ? b.uploadedPhotoUrl : `${process.env.REACT_APP_BACKEND_URL}${b.uploadedPhotoUrl}`} alt="Uploaded ID" className="w-12 h-12 object-cover rounded-md border hover:opacity-80 transition-opacity" />
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
                <select defaultValue={b.bookingStatus} onChange={(e) => updateStatus(b.id, e.target.value)} className="h-7 rounded-sm border border-input bg-transparent px-1 text-[11px]">
                  {["reserved", "confirmed", "payment_pending", "cancelled", "no_show"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select defaultValue={b.paymentStatus} onChange={(e) => updatePayment(b.id, e.target.value)} className="h-7 rounded-sm border border-input bg-transparent px-1 text-[11px]">
                  {["pending", "paid", "failed", "refunded"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {b.whatsappOptIn && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => resendWhatsApp(b.id)} title="Resend WhatsApp">
                    <MessageSquare className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => sendEmail(b.id)} title="Send Email">
                  <Mail className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {bookings.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No bookings yet</p>}
      </div>
    </div>
  );
}
